// Pure helpers for the Edge explorer tree (no React, no API). Text comes from the `edges.*` locale keys.
import i18n from "../../i18n";

export const NO_LOCATION = "none";

export const edgeLabel = (e) => e.name ?? e.edgeId;

export const tenantNodeId = (tenantId) => `t:${tenantId}`;
export const locationNodeId = (tenantId, locationId) => `l:${tenantId}:${locationId || NO_LOCATION}`;

// Visible tenants -> roots (parent not visible) + children map.
export function buildTenantForest(tenants) {
  const ids = new Set(tenants.map((t) => t.id));
  const childrenOf = {};
  const roots = [];
  tenants.forEach((t) => {
    if (t.parent_tenant_id && ids.has(t.parent_tenant_id)) {
      (childrenOf[t.parent_tenant_id] = childrenOf[t.parent_tenant_id] || []).push(t);
    } else {
      roots.push(t);
    }
  });
  return { roots, childrenOf };
}

// tenantId + all descendant tenant ids.
export function tenantSubtreeIds(tenantId, childrenOf) {
  const out = [tenantId];
  (childrenOf[tenantId] || []).forEach((c) => out.push(...tenantSubtreeIds(c.id, childrenOf)));
  return out;
}

const ZERO = { edge_count: 0, online: 0, offline: 0, open_alarms: 0 };

export function sumCounts(list) {
  return list.reduce(
    (a, c) => ({
      edge_count: a.edge_count + (c.edge_count || 0),
      online: a.online + (c.online || 0),
      offline: a.offline + (c.offline || 0),
      open_alarms: a.open_alarms + (c.open_alarms || 0),
    }),
    { ...ZERO }
  );
}

// Merge several /edges/summary responses (one per root) into lookup maps.
// tenants[tid] = direct counts of that tenant, locations["tid:lid|none"] = counts of that location.
export function mergeSummaries(summaries) {
  const out = { tenants: {}, locations: {} };
  summaries.filter(Boolean).forEach((s) => {
    (s.tenants || []).forEach((t) => (out.tenants[t.tenant_id] = t));
    (s.locations || []).forEach((l) => (out.locations[`${l.tenant_id}:${l.location_id || NO_LOCATION}`] = l));
  });
  return out;
}

export const locationCounts = (idx, tid, lid) => (idx ? idx.locations[`${tid}:${lid || NO_LOCATION}`] || ZERO : null);

// Tenant subtree roll-up (own + descendants). null when summary not loaded.
export function tenantCounts(idx, tid, childrenOf) {
  if (!idx) return null;
  return sumCounts(tenantSubtreeIds(tid, childrenOf).map((id) => idx.tenants[id] || ZERO));
}

// Badges for a node from counts: [{kind: "off"|"ok"|"warn", text}]. Nothing while counts are unknown.
export function rollupBadges(c) {
  if (!c) return [];
  const out = [];
  if (c.open_alarms) out.push({ kind: "warn", text: i18n.t("edges.badge.alarm", { n: c.open_alarms }) });
  if (c.offline) out.push({ kind: "off", text: i18n.t("edges.badge.offline", { n: c.offline }) });
  if (c.online) out.push({ kind: "ok", text: i18n.t("edges.badge.online", { n: c.online }) });
  return out;
}

// Selected node <-> ?node= value: tenant:<id> | location:<tenantId>:<locationId|none> | edge:<id>
export function encodeNode(n) {
  if (n.type === "tenant") return `tenant:${n.tenantId}`;
  if (n.type === "location") return `location:${n.tenantId}:${n.locationId || NO_LOCATION}`;
  return `edge:${n.id}`;
}

export function decodeNode(str) {
  if (!str) return null;
  const [type, a, b] = str.split(":");
  if (type === "tenant" && a) return { type, tenantId: a };
  if (type === "location" && a && b) return { type, tenantId: a, locationId: b };
  if (type === "edge" && a) return { type, id: a };
  return null;
}

// Unknown tenant/location -> root. locsByTenant[tid] undefined = not loaded yet, accepted.
export function resolveNode(node, tenantById, rootId, locsByTenant = {}) {
  const root = rootId ? { type: "tenant", tenantId: rootId } : null;
  if (!node) return root;
  if (node.type === "edge") return node;
  if (!tenantById[node.tenantId]) return root;
  if (node.type === "location" && node.locationId !== NO_LOCATION) {
    const locs = locsByTenant[node.tenantId];
    if (locs && !locs.some((l) => l.id === node.locationId)) return root;
  }
  return node;
}

export const stackLabel = (e) => `${e.reported_os_version || "—"} · ${e.stack_version || "—"}`;

// Locations for a tenant: API result, plus ids seen only on edges (403 for sub-tenants, or "none").
export function mergeLocations(apiLocations, edges) {
  const byId = new Map();
  (apiLocations || []).forEach((l) => byId.set(l.id, { id: l.id, name: l.name }));
  edges.forEach((e) => {
    if (e.location_id && !byId.has(e.location_id)) {
      byId.set(e.location_id, { id: e.location_id, name: e.locationName || e.location_id });
    }
  });
  const list = [...byId.values()];
  if (edges.some((e) => !e.location_id)) list.push({ id: NO_LOCATION, name: i18n.t("edges.tree.noLocation") });
  return list;
}

// Add the "no location" bucket when the summary says the tenant has such edges.
export function withNoneBucket(locations, idx, tid) {
  if (locations.some((l) => l.id === NO_LOCATION)) return locations;
  return locationCounts(idx, tid, NO_LOCATION)?.edge_count ? [...locations, { id: NO_LOCATION, name: i18n.t("edges.tree.noLocation") }] : locations;
}

export function formatLastSeen(iso, now = Date.now()) {
  if (!iso) return "—";
  const min = Math.floor((now - new Date(iso).getTime()) / 60000);
  if (Number.isNaN(min)) return "—";
  if (min < 2) return i18n.t("edges.time.justNow");
  if (min < 60) return i18n.t("edges.time.minutesAgo", { n: min });
  if (min < 1440) return i18n.t("edges.time.hoursAgo", { n: Math.floor(min / 60) });
  return i18n.t("edges.time.daysAgo", { n: Math.floor(min / 1440) });
}
